'use client';

import { motion, useInView, AnimatePresence } from 'motion/react';
import { useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  Cpu,
  Sparkles,
  Layout,
  Globe,
  Smartphone,
  Plug,
  Server,
  Database,
  ArrowRight,
  ChevronDown,
} from 'lucide-react';
import { isFinnishPage, WORK_FI, WORK_EN } from '../../../lib/blogPaths';

const SOLUTIONS_EN = [
  {
    icon: Cpu,
    title: 'AI Assisted Development',
    description:
      'All our consultants efficiently leverage a suite of AI tools, including GitHub Copilot, ChatGPT, Gemini, and Claude. This boosts our workflow, translating directly into faster project deliveries for you. Just as importantly, our developers are experts in knowing precisely when not to use AI.',
    workSlug: null,
    workTitle: null,
  },
  {
    icon: Sparkles,
    title: 'Custom AI solutions',
    description:
      "We develop custom AI-powered solutions designed specifically to support your business. Whether it's enhancing the customer experience or leveraging new forms of data, we intelligently integrate AI into your new or existing products and processes.",
    workSlug: null,
    workTitle: null,
  },
  {
    icon: Layout,
    title: 'Software Architecture',
    description:
      'A sustainable and well-designed software architecture ensures that your system scales with your business. This approach minimizes challenges and saves time and money in the long run.',
    workSlug: 'fixably',
    workTitle: 'FIXABLY — Scalability for mobile device after-sales business with a new digital product',
  },
  {
    icon: Globe,
    title: 'Web App Development',
    description:
      "A well-designed web app adapts to users' needs and supports business growth. The right technology choices, along with a scalable and easily maintainable application structure, enable further development and expansion in the future.",
    workSlug: null,
    workTitle: null,
  },
  {
    icon: Smartphone,
    title: 'Mobile Development',
    description:
      'In developing future-proof mobile apps, accessibility, user-friendliness, and modern technologies take center stage.',
    workSlug: 'supli',
    workTitle: 'SUPLI — Case study',
  },
  {
    icon: Plug,
    title: 'Integrations',
    description:
      'Efficient systems require seamless data flow between different softwares and platforms. Integrations reduce manual work and enable the coexistence of legacy and new products as a unified system.',
    workSlug: 'smartum',
    workTitle: 'SMARTUM — Customer understanding as the foundation of digital service development',
  },
  {
    icon: Server,
    title: 'DevOps, Infrastructure, and Cloud Services',
    description:
      'Our consultants work following the best practices of the software industry. We build new secure solutions in the cloud or help you migrate your services there smoothly.',
    workSlug: 'lyyti',
    workTitle: 'LYYTI — Towards modern and more scalable digital event production',
  },
  {
    icon: Database,
    title: 'Data-Intensive Apps',
    description:
      'Well-managed, stored, and utilized data can be more valuable than gold. Our expert developers create scalable data solutions that support long-term business growth and development.',
    workSlug: null,
    workTitle: null,
  },
];

const SOLUTIONS_FI = [
  { icon: Cpu, title: 'AI-tehostettu ohjelmistokehitys', description: 'Saatte enemmän aikaan vähemmällä. Kaikki konsulttimme käyttävät tehokkaasti lukuisia tekoälytyökaluja. Se tehostaa työskentelyä ja näkyy teille nopeampina toimituksina. Kehittäjämme ovat myös ammattilaisia siinä, milloin tekoälyä ei kannata käyttää.', workSlug: null, workTitle: null },
  { icon: Sparkles, title: 'Räätälöidyt tekoälyratkaisut', description: 'Kehitämme tekoälyä hyödyntäviä ratkaisuja, jotka tukevat juuri teidän liiketoimintaanne. Olipa kyse asiakaskokemuksen parantamisesta tai uudenlaisen datan hyödyntämisestä, integroimme tekoälyn järkevästi uusiin tai olemassa oleviin tuotteisiinne ja prosesseihinne.', workSlug: null, workTitle: null },
  { icon: Layout, title: 'Ohjelmistoarkkitehtuuri', description: 'Kestävä ja hyvin suunniteltu ohjelmistoarkkitehtuuri varmistaa järjestelmän skaalautuvuuden liiketoimintanne mukana. Näin minimoimme haasteita ja säästämme aikaa ja rahaa pitkällä aikavälillä.', workSlug: 'fixably', workTitle: 'FIXABLY — Skaalautuvuutta mobiililaitteiden huoltoliiketoimintaan uuden tuotteen avulla' },
  { icon: Globe, title: 'Verkkosovellusten kehitys', description: 'Hyvin suunniteltu verkkosovellus mukautuu käyttäjien tarpeisiin ja tukee liiketoiminnan kasvua. Oikeat teknologiavalinnat sekä skaalautuva ja helposti ylläpidettävä sovellusrakenne mahdollistavat kehittämisen ja laajentamisen myös tulevaisuudessa.', workSlug: null, workTitle: null },
  { icon: Smartphone, title: 'Mobiilikehitys', description: 'Tulevaisuudenkestävien mobiilisovellusten kehittämisessä saavutettavuus, käyttäjäystävällisyys ja modernit teknologiat nousevat keskiöön.', workSlug: 'supli', workTitle: 'SUPLI — Asiakastarina' },
  { icon: Plug, title: 'Integraatiot', description: 'Tehokkaasti toimivat järjestelmät edellyttävät sujuvaa tiedonkulkua eri ohjelmistojen ja järjestelmien välillä. Integraatiot vähentävät manuaalista työtä sekä mahdollistavat vanhan ja uuden tuotteen toimivuuden päällekkäin yhtenä järjestelmänä.', workSlug: 'smartum', workTitle: 'SMARTUM — Asiakasymmärryksestä digitaalisen palvelukehityksen perusta' },
  { icon: Server, title: 'DevOps, infrastruktuuri ja pilvipalvelut', description: 'Monipuolista kokemusta omaavat konsulttimme työskentelevät ohjelmistoalan parhaita käytäntöjä noudattaen. Rakennamme uudet turvalliset ratkaisut pilveen tai autamme palveluidesi siirtämisessä sinne sujuvasti.', workSlug: 'lyyti', workTitle: 'LYYTI — Kohti nykyaikaisempaa ja skaalautuvampaa digitaalista tapahtumatuotantoa' },
  { icon: Database, title: 'Dataintensiiviset sovellukset', description: 'Hyvin hallinoitu, talletettu ja hyödynnetty data voi olla kultaakin kalliimpaa. Asiantuntevat kehittäjämme luovat skaalautuvia dataratkaisuja, jotka tukevat pitkäjänteistä liiketoiminnan kasvua ja kehitystä.', workSlug: null, workTitle: null },
];

export function SoftwareDevSolutionsSection() {
  const ref = useRef(null);
  const router = useRouter();
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [openIndex, setOpenIndex] = useState(0);
  const isFinnish = isFinnishPage(router?.pathname);
  const solutions = isFinnish ? SOLUTIONS_FI : SOLUTIONS_EN;
  const heading = isFinnish ? 'Ratkaisut liiketoimintaanne' : 'Solutions for your business';
  const workBase = isFinnish ? WORK_FI : WORK_EN;
  const readStoryLabel = isFinnish ? 'Lue asiakastarina' : 'Read customer story';

  return (
    <section ref={ref} className="section-rhythm bg-white">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h2 className="heading-2 mb-4">{heading}</h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.05)]"
        >
          {solutions.map((item, index) => {
            const Icon = item.icon;
            const isOpen = openIndex === index;
            return (
              <div
                key={`${item.title}-${index}`}
                className={`border-b border-gray-100 last:border-b-0 ${isOpen ? 'bg-[#fafbff]' : 'bg-white hover:bg-gray-50/80'}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 rounded-none"
                  aria-expanded={isOpen}
                  aria-controls={`solution-panel-${index}`}
                  id={`solution-trigger-${index}`}
                >
                  <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#e2e8f0] text-[#0f172a] shrink-0">
                    <Icon className="w-5 h-5" aria-hidden />
                  </span>
                  <span className="flex-1 font-semibold text-gray-900">{item.title}</span>
                  <motion.span
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="shrink-0 text-gray-400"
                  >
                    <ChevronDown className="w-5 h-5" aria-hidden />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      id={`solution-panel-${index}`}
                      role="region"
                      aria-labelledby={`solution-trigger-${index}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 pt-0 pl-14 sm:pl-14">
                        <p className="text-[15px] text-gray-600 leading-relaxed">
                          {item.description}
                        </p>
                        {item.workSlug && item.workTitle && (
                          <Link
                            href={`${workBase}/${item.workSlug}`}
                            className="inline-flex items-center gap-2 mt-4 text-brand font-medium text-sm hover:underline"
                          >
                            {readStoryLabel}
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
